import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearch } from '@/hooks/useSearch';
import { Protocol, User } from 'authing-js-sdk';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getDingtalkAppAuthUrl, getDisplayName, getSocialAuthUrl, isDingtalkBrowser, isLarkBrowser, isWeComeBrowser } from '@/utils';
import querystring from "query-string";
import { PageFooter } from '@/components/PageFooter';
import { changeLang, i18n } from '@/locales';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { Spin } from '@/components/Loading/ShieldSpin';
import { insertStyles } from './insertStyles';
import { SocialConnectionProvider } from '@/config';

export const LoginV2: React.FC = () => {

    require('@authing/react-ui-components/lib/index.min.css');
    const { Guard } = require('@authing/react-ui-components')

    const config = window.__config__;
    const host = window.location.origin

    const submitBtn = useRef<any>();
    const { t } = useTranslation()
    const search = useSearch()
    const [appId, setAppId] = useState<string>('')
    const [tenantId, setTenantId] = useState<string>('')
    const [formHidden, setFormHidden] = useState({
        action: "",
        token: "",
    });
    const [fields, setFields] = useState<{ [key: string]: string }>({});
    const navigate = useNavigateWithSearch();
    const [loading, setLoading] = useState<boolean>(true)
    const [showFooter, setShowFooter] = useState(false)
    const [callBackUrl, setCallBackUrl] = useState<string>("")
    const contentCss = window.__config__.css;

    // 飞书企业自建应用在飞书内打开直接登录
    const larkInnerAppLogin = config?.socialConnections?.find(
        (item) => item.provider === SocialConnectionProvider.LARK_INTERNAL
    );
    if (larkInnerAppLogin && isLarkBrowser()) {
        window.location.href = getSocialAuthUrl(larkInnerAppLogin);
        return <Spin />;
    }

    // 企业微信内部应用在企业微信内打开直接登录
    const wecomeInnerAppLogin = config?.socialConnections?.find(
        (item) =>
            item.provider === SocialConnectionProvider.WECHATWORK_CORP_QRCONNECT
    );
    if (wecomeInnerAppLogin && isWeComeBrowser()) {
        window.location.href = getSocialAuthUrl(wecomeInnerAppLogin);
        return <Spin />;
    }

    // dīng ding 内部应用在钉钉内打开直接登录
    const dingtalkInnerAppLogin = config?.socialConnections?.find(
        (item) => item.provider === SocialConnectionProvider.DINGTALK
    );
    if (
        dingtalkInnerAppLogin &&
        dingtalkInnerAppLogin.corpId &&
        isDingtalkBrowser()
    ) {
        window.location.href = getDingtalkAppAuthUrl(dingtalkInnerAppLogin, dingtalkInnerAppLogin.identifier);
        return <Spin />;
    }


    useEffect(() => {
        setAppId(search?.app_id as string)
        setTenantId(search?.tenant_id as string)
        setCallBackUrl(search?.["finish_login_url"] as string)
    }, [appId, search])

    const lang = useMemo(() => i18n.language, [])

    const onLangChange = ({ key }: any) => {
        changeLang(key);
    };

    useEffect(() => {
        insertStyles(contentCss);
    }, [contentCss])

    useEffect(() => {
        const query = querystring.parse(window.location.search);
        const path = query["goto"];
        if (path) {
            navigate(path as string);
        }
    }, [config, navigate]);

    useEffect(() => {
        if (appId)
            setLoading(false)
    }, [appId])

    const onSuccess = async (user: User) => {

        await window.__authing__.setCurrentUser(user);

        message.success(
            t("common.loginSuccessWelcome", {
                name: getDisplayName(user),
            })
        );

        setTimeout(() => {
            // TODO: 这段逻辑与 src/utils/loginSuccess.tsx 的 loginSuccessRedirect 一致，谁敢替换一下
            if (config.protocol) {
                setFormHidden({
                    action: callBackUrl,
                    token: user.token ?? "",
                });
                if (config.protocol === Protocol.OAUTH) {
                    setFields(querystring.parse(window.location.search) as any);
                }
                submitBtn.current.click();
                return;
            }
            // if (config.protocol === Protocol.OIDC) {
            //     const query = querystring.parse(window.location.search);
            //     setFormHidden({
            //         action: `/interaction/oidc/${query["uuid"]}/login`,
            //         token: user.token ?? "",
            //     });
            //     submitBtn.current.click();
            //     return;
            // }

            // if (config.protocol === Protocol.SAML) {
            //     setFormHidden({
            //         action: `/interaction/saml-idp/login`,
            //         token: user.token ?? "",
            //     });
            //     submitBtn.current.click();
            //     return;
            // }

            // if (config.protocol === Protocol.CAS) {
            //     setFormHidden({
            //         action: `/interaction/cas-idp/login`,
            //         token: user.token ?? "",
            //     });
            //     submitBtn.current.click();
            //     return;
            // }

            // if (config.protocol === Protocol.OAUTH) {
            //     setFormHidden({
            //         action: `/interaction/oauth/login`,
            //         token: user.token ?? "",
            //     });
            //     setFields(querystring.parse(window.location.search) as any);
            //     submitBtn.current.click();
            //     return;
            // }

            // protocol = none 时，直接带着用户信息跳转过去
            const redirectUrl = `${config.redirectUris && config.redirectUris[0]
                }/?data=${encodeURIComponent(JSON.stringify(user))}`;
            window.location.href = redirectUrl;
        }, 500);
    };
    return (
        <>
            {
                loading ?
                    <Spin />
                    : <>
                        <Guard
                            appId={appId}
                            config={{ host, lang, contentCss: "", isHost: true }}
                            tenantId={tenantId}
                            onLogin={(user: any) => onSuccess(user)}
                            onLangChange={(key: any) => {
                                setLoading(true)
                                onLangChange({ key })
                            }}
                            onLoad={() => {
                                setShowFooter(true)
                            }}
                        />
                        {showFooter && <PageFooter />}
                    </>
            }
            <form
                action={formHidden.action}
                method="post"
                style={{ display: "none" }}
            >
                {Object.entries(fields).map(([k, v]) => (
                    <input key={k} type="text" name={k} value={v} readOnly />
                ))}
                <input type="text" name="token" value={formHidden.token} readOnly />
                <button ref={submitBtn} type="submit" />
            </form>
        </>
    );
};
